import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpedientes } from '../lib/hooks';
import type { EstadoExpediente } from '../lib/phase1';
import { Pill, Skeleton, cn } from '../components/ui';
import { PersonaIcon } from '../components/icons';
import { BigButton, friendlyEstado } from '../components/flow';
import { Search, ChevronRight, UserPlus, Users } from 'lucide-react';

/**
 * "Mis clientes" — WhatsApp-style tappable card list (no table). Plain-language
 * status via friendlyEstado. Renders as plain page content inside the app shell
 * (AppLayout provides the top bar + sidebar).
 */

const PENDIENTE: EstadoExpediente[] = [
  'Nuevo',
  'En Revisión',
  'Docs Pendientes',
  'Escalado',
  'Actualización Requerida',
];
const LISTO: EstadoExpediente[] = ['Aprobado', 'Activo'];

const FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'pendiente', label: 'Pendientes' },
  { key: 'listo', label: 'Listos' },
];

export default function Expedientes() {
  const navigate = useNavigate();
  const { data: rows, isLoading } = useExpedientes();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      const bucket =
        filter === 'all' ||
        (filter === 'pendiente' && PENDIENTE.includes(r.estado)) ||
        (filter === 'listo' && LISTO.includes(r.estado));
      const matches = term === '' || r.name.toLowerCase().includes(term) || r.docId.toLowerCase().includes(term);
      return bucket && matches;
    });
  }, [rows, filter, q]);

  return (
    <>
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-ink sm:text-4xl">Mis clientes</h1>
          <p className="mt-1 text-base text-ink-soft">{rows?.length ?? 0} en total</p>
        </div>
        <BigButton full={false} className="hidden sm:inline-flex" onClick={() => navigate('/expedientes/nuevo')}>
          <UserPlus className="h-5 w-5" strokeWidth={2} /> Nuevo
        </BigButton>
      </div>

      {/* search */}
      <div className="relative mt-6">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-faint" strokeWidth={2} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o cédula…"
          className="h-14 w-full rounded-2xl border-2 border-line-strong bg-panel pl-12 pr-4 text-base text-ink shadow-card placeholder:text-ink-faint focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
        />
      </div>

      {/* friendly filter buckets */}
      <div className="mt-4 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              'rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors',
              filter === f.key
                ? 'border-primary bg-primary/10 text-primary-dark'
                : 'border-line-strong bg-panel text-ink-soft hover:border-ink-faint'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* list */}
      <div data-tour="client-card" className="mt-4 grid gap-3 [&>*]:min-w-0">
        {isLoading &&
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[88px] rounded-2xl" />)}

        {!isLoading && filtered.length === 0 && (
          <div className="grid place-items-center gap-3 rounded-2xl border border-line bg-panel px-6 py-16 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-canvas text-ink-faint">
              <Users className="h-7 w-7" strokeWidth={1.5} />
            </span>
            <div className="font-serif text-lg text-ink">Sin clientes aquí</div>
            <p className="max-w-xs text-sm text-ink-soft">
              {q ? 'Prueba con otro nombre o cédula.' : 'Registra tu primer cliente para empezar.'}
            </p>
            <BigButton full={false} className="mt-1" onClick={() => navigate('/expedientes/nuevo')}>
              <UserPlus className="h-5 w-5" strokeWidth={2} /> Registrar cliente
            </BigButton>
          </div>
        )}

        {!isLoading &&
          filtered.map((r) => {
            const fe = friendlyEstado(r.estado);
            return (
              <button
                key={r.id}
                onClick={() => navigate(`/expedientes/${r.id}`)}
                className="flex items-center gap-4 rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-all hover:border-ink-faint active:scale-[.99]"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10">
                  <PersonaIcon persona={r.persona} className="h-6 w-6 text-primary-dark" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-lg font-bold text-ink">{r.name}</span>
                  <span className="mt-1 flex flex-wrap items-center gap-2">
                    <Pill tone={fe.tone}>{fe.label}</Pill>
                    {r.pep && <Pill tone="red">PEP</Pill>}
                  </span>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-ink-faint" strokeWidth={2} />
              </button>
            );
          })}
      </div>

      {/* mobile-friendly bottom action */}
      <BigButton className="mt-6 sm:hidden" onClick={() => navigate('/expedientes/nuevo')}>
        <UserPlus className="h-5 w-5" strokeWidth={2} /> Registrar nuevo cliente
      </BigButton>
    </>
  );
}
